import { useCallback, useRef } from 'react';
import { css } from '@linaria/atomic';
import { Modal, ModalControlModalProps } from '@frontend/design-system';
import { PaymentMethodSetupFooter } from '../components/payment-method-setup/footer/footer';
import { PaymentMethodSetupForm } from '../components/payment-method-setup/form/form';
import type { PaymentMethodSetupProviderProps } from '../widgets/payment-method-setup/context/payment-method-setup-base.context';
import { PaymentMethodSetupProvider } from '../widgets/payment-method-setup/context/payment-method-setup.context';
import type { SavedPaymentMethod } from '../widgets/payment-method-setup/types';

const MODAL_WIDTH = 480;

export type PaymentMethodSetupModalProps = Omit<PaymentMethodSetupProviderProps, 'onCancel' | 'children'> & {
  modalProps: ModalControlModalProps;
  title: React.ReactNode;
  minWidth?: number;
  maxWidth?: number;
};

/** Saves one card or bank account (no charge). Pass a fresh session from `usePreparePaymentMethodSetup` on each open. */
export const PaymentMethodSetupModal = ({
  modalProps,
  title,
  minWidth = MODAL_WIDTH,
  maxWidth = MODAL_WIDTH,
  onSaved,
  ...props
}: PaymentMethodSetupModalProps) => {
  const { onClose } = modalProps;
  const isSavingRef = useRef(false);

  const handleSavingChange = useCallback((isSaving: boolean) => {
    isSavingRef.current = isSaving;
  }, []);

  // Header close and Escape stay inert while a save is confirming, so a stale save can't land in a new session.
  const handleDismiss = useCallback(() => {
    if (!isSavingRef.current) onClose();
  }, [onClose]);

  const handleSaved = useCallback(
    (method: SavedPaymentMethod) => {
      onSaved(method);
      onClose();
    },
    [onClose, onSaved]
  );

  return (
    <Modal
      {...modalProps}
      onClose={handleDismiss}
      className={modalStyle}
      disableCloseOnOverlayClick
      minWidth={minWidth}
      maxWidth={maxWidth}
    >
      <Modal.Header onClose={handleDismiss}>{title}</Modal.Header>
      {modalProps.show && (
        <PaymentMethodSetupProvider
          {...props}
          onSaved={handleSaved}
          onCancel={onClose}
          onSavingChange={handleSavingChange}
        >
          <Modal.Body>
            <PaymentMethodSetupForm />
          </Modal.Body>
          <PaymentMethodSetupFooter />
        </PaymentMethodSetupProvider>
      )}
    </Modal>
  );
};

const modalStyle = css`
  && {
    padding-top: var(--fabric-space-padding-md);
    padding-bottom: var(--fabric-space-padding-md);
  }

  & .wv-modal-header,
  & .wv-modal-flex-content,
  & .wv-modal-footer {
    padding-left: var(--fabric-space-padding-md);
    padding-right: var(--fabric-space-padding-md);
  }
`;
